import type { Control } from "../core/Control.js";
import type { Alignment, Insets, Point, Rect, Size } from "../../common/geometry.js";
import { LayoutPanel, type CrossAlign, type LayoutPanelOptions } from "./LayoutPanel.js";

/**
 * A track size: `"Auto"`, `"*"`, `"2*"`, or a number of pixels such as `"120"`.
 *
 * A string rather than a union of shapes because this is the form it is written in — in markup,
 * in a property grid, in the one place a person types it — and parsing it once at layout time
 * costs nothing next to the arranging.
 */
export type GridLength = string;

/** `"50%"` of what the grid was given. Sized before the star tracks divide up what is left. */

export interface GridPlacement {
  row?: number;
  column?: number;
  /** Cells spanned, at least 1. */
  rowSpan?: number;
  columnSpan?: number;
  /** How the child sits in its cell. Falls back to the grid's `align`. */
  hAlign?: CrossAlign;
  vAlign?: CrossAlign;
}

export interface GridOptions extends LayoutPanelOptions {
  rows?: GridLength[];
  columns?: GridLength[];
}

interface Track {
  kind: "auto" | "star" | "fixed" | "percent";
  value: number;
}

const ONE_STAR: Track = { kind: "star", value: 1 };

/**
 * Rows and columns, WPF's way.
 *
 * The point of a grid is that nothing inside it carries a coordinate: a child says which cell
 * it is in and how it sits there, and every position is derived. Move a column and everything
 * in it moves; resize the window and the star columns take the slack. That is the whole
 * difference between a layout that survives being edited and one that has to be nudged back
 * into place by hand.
 *
 * Sizing works within the framework's one constraint: there is no measure pass, so a child's
 * *desired* size is whatever size it currently has. `Auto` therefore means "as wide as the
 * widest thing already in this column", which is the honest reading of it here. A child that
 * spans several tracks is left out of their `Auto` measurements - WPF distributes such a child
 * across the tracks it covers, which needs a second pass and rarely changes the answer for the
 * layouts anyone actually writes.
 *
 * `spacing` is a gutter between tracks. WPF has no such thing and asks for margins instead, but
 * a gutter is what people mean nine times in ten, and saying it once beats saying it on every
 * child.
 */
export class Grid extends LayoutPanel {
  rows: GridLength[];
  columns: GridLength[];

  #placement = new WeakMap<Control, GridPlacement>();
  #columnWidths: number[] = [];
  #rowHeights: number[] = [];

  constructor(options: GridOptions = {}) {
    super(options);
    this.rows = options.rows ?? ["*"];
    this.columns = options.columns ?? ["*"];
    this.performLayout();
  }

  /** Add a child, optionally saying where it goes. */
  add<T extends Control>(control: T, placement?: GridPlacement): T {
    if (placement) this.#placement.set(control, placement);
    this.controls.add(control);
    return control;
  }

  setPlacement(control: Control, placement: GridPlacement): void {
    this.#placement.set(control, placement);
    this.performLayout();
    this.invalidate();
  }

  placementOf(control: Control): GridPlacement {
    return this.#placement.get(control) ?? {};
  }

  /** Track sizes as of the last layout. */
  get columnWidths(): readonly number[] {
    return this.#columnWidths;
  }

  get rowHeights(): readonly number[] {
    return this.#rowHeights;
  }

  /** The rectangle a placement occupies, in child coordinates. */
  cellRect(placement: GridPlacement): Rect {
    const column = clampIndex(placement.column, this.columns.length);
    const row = clampIndex(placement.row, this.rows.length);
    const columnSpan = clampSpan(placement.columnSpan, this.columns.length - column);
    const rowSpan = clampSpan(placement.rowSpan, this.rows.length - row);

    const x = offsetOf(this.#columnWidths, column, this.spacing);
    const y = offsetOf(this.#rowHeights, row, this.spacing);
    return {
      x,
      y,
      width: extentOf(this.#columnWidths, column, columnSpan, this.spacing),
      height: extentOf(this.#rowHeights, row, rowSpan, this.spacing),
    };
  }

  /**
   * The cell a point in *child* space falls in.
   *
   * Clamped, so a point past the last track still names a real cell: this answers "where would
   * this land", and a drag that strays a few pixels outside the grid still meant somewhere.
   * A point over a gutter belongs to the track before it, which is what makes dropping onto the
   * seam between two cells behave rather than refuse.
   */
  cellAt(p: Point): { row: number; column: number } {
    return {
      row: trackAt(this.#rowHeights, this.spacing, p.y),
      column: trackAt(this.#columnWidths, this.spacing, p.x),
    };
  }

  // --- layout ----------------------------------------------------------

  /**
   * Size every track, columns before rows.
   *
   * The order is the whole reason a wrapping label in a star column ends up in a row tall enough
   * to hold it: by the time an `Auto` row asks a child how tall it is, the child can be told how
   * wide its cell already is. Going the other way round would need a second pass and would still
   * be guessing.
   *
   * `starAsAuto` is for measuring rather than arranging. A star track is a share of space the
   * grid has been given, and a grid being measured has not been given any yet, so the honest
   * answer is the one WPF gives an unconstrained measure: size it to its content.
   *
   * `shareColumns` keeps the star *columns* sharing the width offered even so, and is how a
   * measure works out its height. The width is the question a height is asked with - "how tall
   * at this width" - and a wrapping label measured in a column sized to its content is measured
   * at whatever width it last had: too few lines, on a grid made narrower, and a row that clips.
   */
  #resolve(available: Size, starAsAuto: boolean, shareColumns = false): { columns: number[]; rows: number[] } {
    const children = this.laidOutChildren();
    const placements = children.map((child) => this.#resolved(child));
    const specs = (list: readonly GridLength[]): GridLength[] =>
      starAsAuto ? list.map((g) => (parseGridLength(g).kind === "star" ? "Auto" : g)) : [...list];

    const columns = resolveTracks(shareColumns ? [...this.columns] : specs(this.columns), available.width, this.spacing, (i) =>
      this.#autoExtent(children, placements, i, "x", available, null),
    );
    const rows = resolveTracks(specs(this.rows), available.height, this.spacing, (i) =>
      this.#autoExtent(children, placements, i, "y", available, columns),
    );
    return { columns, rows };
  }

  protected override arrange(available: Size): Size {
    // `rows` doubles as the "constructed enough" sentinel, the way orientation does upstream.
    if (!this.rows || !this.columns) return { width: 0, height: 0 };

    const children = this.laidOutChildren();
    const placements = children.map((child) => this.#resolved(child));

    const tracks = this.#resolve(available, false);
    this.#columnWidths = tracks.columns;
    this.#rowHeights = tracks.rows;

    children.forEach((child, i) => {
      const placement = placements[i]!;
      const cell = this.cellRect(placement);
      const margin = child.margin;
      const natural = this.naturalSize(child, {
        width: Math.max(0, cell.width - margin.left - margin.right),
        height: Math.max(0, cell.height - margin.top - margin.bottom),
      });

      const inner = {
        x: cell.x + margin.left,
        y: cell.y + margin.top,
        width: Math.max(0, cell.width - margin.left - margin.right),
        height: Math.max(0, cell.height - margin.top - margin.bottom),
      };

      const h = place(placement.hAlign ?? this.align, inner.width, natural.width);
      const v = place(placement.vAlign ?? this.align, inner.height, natural.height);
      child.setBounds(inner.x + h.offset, inner.y + v.offset, h.size, v.size);
    });

    return {
      width: totalOf(this.#columnWidths, this.spacing),
      height: totalOf(this.#rowHeights, this.spacing),
    };
  }

  /**
   * The tracks the content needs, with every star track sized to its content - except that the
   * rows are worked out at the width offered, which is the width the content will wrap at.
   */
  protected override measure(available: Size): Size {
    if (!this.rows || !this.columns) return { width: 0, height: 0 };
    const across = this.#resolve(available, true);
    const down = this.columns.some((c) => parseGridLength(c).kind === "star")
      ? this.#resolve(available, true, true)
      : across;
    return {
      width: totalOf(across.columns, this.spacing),
      height: totalOf(down.rows, this.spacing),
    };
  }

  /**
   * Grow to the tracks - on an axis nobody else has already been handed.
   *
   * Two things hand one away, and they are the same thing said twice. A `*` track is a share of
   * what the grid was *given*, so an axis carrying one resolves to exactly the space available
   * and measuring it says nothing; the one time the sum differs is a grid too small for its
   * fixed tracks, where growing would push the overflow out through the parent rather than
   * clipping it inside the grid. And a `stretch` is the parent being told to decide, which it
   * cannot do if the child resizes itself the moment it is stretched.
   *
   * What is left is an axis of `Auto` and fixed tracks in a control nobody is stretching, which
   * knows its own size exactly - and is what `autoSize` is for.
   */
  protected override applyAutoSize(content: Size): void {
    const padding = this.padding;
    const own = (
      specs: readonly GridLength[],
      align: Alignment | null,
      measured: number,
      current: number,
    ): number => (align === "stretch" || hasStarTrack(specs) ? current : measured);

    this.setBounds(
      this.x,
      this.y,
      own(this.columns, this.hAlign, content.width + padding.left + padding.right, this.width),
      own(this.rows, this.vAlign, content.height + padding.top + padding.bottom, this.height),
    );
  }

  /** A placement with every index clamped into the grid it is actually in. */
  #resolved(child: Control): Required<Pick<GridPlacement, "row" | "column" | "rowSpan" | "columnSpan">> &
    GridPlacement {
    const raw = this.#placement.get(child) ?? {};
    const column = clampIndex(raw.column, this.columns.length);
    const row = clampIndex(raw.row, this.rows.length);
    return {
      ...raw,
      column,
      row,
      columnSpan: clampSpan(raw.columnSpan, this.columns.length - column),
      rowSpan: clampSpan(raw.rowSpan, this.rows.length - row),
    };
  }

  /**
   * How much an `Auto` track needs: the largest child sitting in it alone.
   *
   * `columns`, once the columns are sized, is what lets a child that reflows be asked the right
   * question — a label measured at the width of the cell it is actually in, rather than at the
   * width of the whole grid.
   */
  #autoExtent(
    children: readonly Control[],
    placements: readonly GridPlacement[],
    index: number,
    axis: "x" | "y",
    available: Size,
    columns: readonly number[] | null,
  ): number {
    let most = 0;
    children.forEach((child, i) => {
      const placement = placements[i]!;
      const at = axis === "x" ? placement.column : placement.row;
      const span = axis === "x" ? placement.columnSpan : placement.rowSpan;
      if (at !== index || (span ?? 1) !== 1) return;

      const margin = child.margin;
      const cellWidth = columns
        ? totalOf(
            columns.slice(placement.column ?? 0, (placement.column ?? 0) + (placement.columnSpan ?? 1)),
            this.spacing,
          )
        : available.width;
      const natural = this.naturalSize(child, {
        width: Math.max(0, cellWidth - margin.left - margin.right),
        height: Math.max(0, available.height - margin.top - margin.bottom),
      });
      const wanted =
        axis === "x"
          ? natural.width + margin.left + margin.right
          : natural.height + margin.top + margin.bottom;
      most = Math.max(most, wanted);
    });
    return most;
  }
}

// --- the arithmetic ----------------------------------------------------------

/**
 * Turn track specs into pixel sizes.
 *
 * Fixed tracks take what they asked for, `Auto` takes what `autoOf` reports, and the star
 * tracks share whatever is left in proportion to their weights. When there is nothing left they
 * get zero rather than a negative: a layout that has run out of room should crowd, not invert.
 *
 * Pure, because this is the part that is easy to get wrong by a pixel and tedious to check by
 * eye - and because a grid is the one control where "why is that column that wide" needs an
 * answer you can point at.
 */
export function resolveTracks(
  specs: readonly GridLength[],
  available: number,
  spacing: number,
  autoOf: (index: number) => number,
): number[] {
  if (specs.length === 0) return [];

  const tracks = specs.map(parseGridLength);
  const gapTotal = spacing * Math.max(0, specs.length - 1);
  // A percentage is of the room the tracks actually divide up, gutters already taken out, so
  // two 50% tracks in a spaced grid still fit side by side instead of overflowing by the gap.
  const divisible = Math.max(0, available - gapTotal);
  const sizes = tracks.map((track, i) => {
    if (track.kind === "fixed") return track.value;
    if (track.kind === "auto") return Math.max(0, autoOf(i));
    if (track.kind === "percent") return Math.max(0, divisible * track.value);
    return 0;
  });

  const gaps = gapTotal;
  const taken = sizes.reduce((total, size) => total + size, 0);
  const spare = Math.max(0, available - gaps - taken);

  const weight = tracks.reduce((total, t) => total + (t.kind === "star" ? t.value : 0), 0);
  if (weight > 0) {
    // Handed out one track at a time from what is left, so rounding cannot lose or invent a
    // pixel: the last star track gets exactly the remainder.
    let remaining = spare;
    let remainingWeight = weight;
    tracks.forEach((track, i) => {
      if (track.kind !== "star") return;
      const share = Math.round((remaining * track.value) / remainingWeight);
      sizes[i] = share;
      remaining -= share;
      remainingWeight -= track.value;
    });
  }

  return sizes;
}

/**
 * Whether any of these tracks divides up space rather than knowing its own size.
 *
 * The question `autoSize` turns on, and the one a caller has to ask before treating a grid's
 * stored width or height as a number that decides anything.
 */
export function hasStarTrack(specs: readonly GridLength[]): boolean {
  return specs.some((spec) => parseGridLength(spec).kind === "star");
}

/** `"Auto"`, `"*"`, `"2.5*"`, `"50%"` or a pixel count. Anything unreadable is treated as `Auto`. */
export function parseGridLength(spec: GridLength): Track {
  const text = spec.trim();
  if (text.length === 0 || /^auto$/i.test(text)) return { kind: "auto", value: 0 };

  if (text.endsWith("%")) {
    const share = Number.parseFloat(text.slice(0, -1).trim());
    return Number.isFinite(share) && share >= 0
      ? { kind: "percent", value: share / 100 }
      : { kind: "auto", value: 0 };
  }

  if (text.endsWith("*")) {
    const head = text.slice(0, -1).trim();
    if (head.length === 0) return ONE_STAR;
    const weight = Number.parseFloat(head);
    return Number.isFinite(weight) && weight > 0 ? { kind: "star", value: weight } : ONE_STAR;
  }

  const px = Number.parseFloat(text);
  return Number.isFinite(px) && px >= 0 ? { kind: "fixed", value: px } : { kind: "auto", value: 0 };
}

/** Which track a distance along the axis falls in, clamped to the ones that exist. */
function trackAt(sizes: readonly number[], spacing: number, at: number): number {
  if (sizes.length === 0) return 0;
  let edge = 0;
  for (let i = 0; i < sizes.length; i += 1) {
    edge += sizes[i]! + spacing;
    if (at < edge) return i;
  }
  return sizes.length - 1;
}

function offsetOf(sizes: readonly number[], index: number, spacing: number): number {
  let at = 0;
  for (let i = 0; i < index && i < sizes.length; i += 1) at += sizes[i]! + spacing;
  return at;
}

function extentOf(
  sizes: readonly number[],
  index: number,
  span: number,
  spacing: number,
): number {
  let total = 0;
  for (let i = index; i < index + span && i < sizes.length; i += 1) total += sizes[i]!;
  // The gutters *inside* a span belong to the child that covers them.
  return total + spacing * Math.max(0, Math.min(span, sizes.length - index) - 1);
}

function totalOf(sizes: readonly number[], spacing: number): number {
  if (sizes.length === 0) return 0;
  return sizes.reduce((a, b) => a + b, 0) + spacing * (sizes.length - 1);
}

function clampIndex(value: number | undefined, count: number): number {
  const at = Math.floor(value ?? 0);
  return Math.max(0, Math.min(Math.max(0, count - 1), Number.isFinite(at) ? at : 0));
}

function clampSpan(value: number | undefined, room: number): number {
  const span = Math.floor(value ?? 1);
  return Math.max(1, Math.min(Math.max(1, room), Number.isFinite(span) ? span : 1));
}

/** Where a child sits within the space its cell gave it. */
function place(align: CrossAlign, available: number, natural: number): {
  offset: number;
  size: number;
} {
  switch (align) {
    case "stretch":
      return { offset: 0, size: available };
    case "center":
      return { offset: Math.round((available - natural) / 2), size: natural };
    case "end":
      return { offset: available - natural, size: natural };
    case "start":
    default:
      return { offset: 0, size: natural };
  }
}

/** Insets with nothing in them, for a caller that has no margin to give. */
export const NO_MARGIN: Insets = { left: 0, top: 0, right: 0, bottom: 0 };
